import { useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import type { SubmissionStatus } from "@colab/shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth-context";
import { useAcceptSubmission } from "./use-accept-submission";
import { useDeclineSubmission } from "./use-decline-submission";

// Phase 7 sub-bucket 17 — SPA port of components/admin/submissions/DecisionPanel.tsx.
// Active only when status === 'pending' and the caller holds
// `submissions.review`. Decline reveals an inline note input before
// confirming.
//
// The mutation hooks (use-accept-submission + use-decline-submission)
// are Phase 6 bucket 8 carry-overs; sub-bucket 17 extended their
// invalidation matrix to flush `submissions.aggregate()` so the queue
// page's metric cards update on next render.

export function DecisionPanel({
  submissionId,
  status,
  existingNote,
}: {
  submissionId: string;
  status: SubmissionStatus;
  existingNote: string | null;
}) {
  const session = useAuth().session;
  const canReview = !!session?.permissions.includes("submissions.review");
  const acceptMutation = useAcceptSubmission();
  const declineMutation = useDeclineSubmission();
  const [declineOpen, setDeclineOpen] = useState(false);
  const [note, setNote] = useState("");

  const pending = acceptMutation.isPending || declineMutation.isPending;

  if (status !== "pending") {
    return (
      <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
        {status === "accepted" ? (
          <>
            <span className="font-medium text-foreground">Accepted.</span>{" "}
            Downstream object created.
          </>
        ) : (
          <>
            <span className="font-medium text-foreground">Declined.</span>
            {existingNote ? <span> &ldquo;{existingNote}&rdquo;</span> : null}
          </>
        )}
      </div>
    );
  }

  if (!canReview) {
    return (
      <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
        You don&apos;t have permission to review submissions. Ask an admin to
        accept or decline this submission.
      </div>
    );
  }

  function accept() {
    acceptMutation.mutate(
      { id: submissionId },
      {
        onSuccess: (data) => {
          toast.success(
            data.kind === "deal" ? "Accepted — deal created" : "Accepted",
          );
        },
      },
    );
  }

  function decline() {
    const trimmed = note.trim();
    declineMutation.mutate(
      {
        id: submissionId,
        note: trimmed.length > 0 ? trimmed : undefined,
      },
      {
        onSuccess: () => {
          toast.success("Declined");
          setDeclineOpen(false);
          setNote("");
        },
      },
    );
  }

  return (
    <div className="space-y-3 rounded-md border bg-card p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium">Awaiting review</div>
          <div className="text-xs text-muted-foreground">
            Accept to create the downstream object, or decline with a note.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeclineOpen((o) => !o)}
            disabled={pending}
          >
            <X className="mr-1 h-3.5 w-3.5" />
            Decline
          </Button>
          <Button size="sm" onClick={accept} disabled={pending}>
            {acceptMutation.isPending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="mr-1 h-3.5 w-3.5" />
            )}
            Accept
          </Button>
        </div>
      </div>
      {declineOpen ? (
        <div className="space-y-2 border-t pt-3">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note (sent to the partner)…"
            rows={3}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDeclineOpen(false);
                setNote("");
              }}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={decline}
              disabled={pending}
            >
              {declineMutation.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : null}
              Confirm decline
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
